/**
 * ! To remove with flag-ue-ds-embed cleanup
 */

import PropTypes from 'prop-types';
import { useMemo } from 'react';
import Dotdotdot from 'react-dotdotdot';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { ButtonClose } from '@hivebrite/design-system/Button';
import { DefaultLink } from '@hivebrite/design-system/Link';
import { Loader } from '@hivebrite/design-system/Loader';

import Preview from './Preview';
import { getAspectRatio } from './utils/embedlyUtils';
import { extractWebsite } from './utils/extractWebsite';

const EmbedlyContent = ({
  autoplay = false,
  customRenderForEmptyImage,
  embed,
  hideDescription,
  isDisabled,
  onClose,
  openPlayVideo,
  roundedEmbed = true,
  externalUrl,
}) => {
  const {
    description,
    height,
    provider_name: providerName,
    provider_url: providerUrl,
    thumbnail_url: thumbnailUrl,
    title,
    type,
    url,
    width,
  } = embed;

  const displayHeader = useMemo(
    () => embed?.thumbnail_url || customRenderForEmptyImage,
    [embed, customRenderForEmptyImage],
  );
  const isEmbedVideoOnHtml = useMemo(
    () => !!embed && embed.type === 'video' && !!embed.html,
    [embed],
  );

  const isVimeoWhitelistVideo =
    !thumbnailUrl && providerName === 'Vimeo' && type === 'video';

  const aspectRatio = getAspectRatio({ width, height });

  return (
    <>
      {displayHeader && (
        <Box position="relative" width="100%" height="100%">
          {onClose && (
            <WrapperBtnClose>
              <ButtonClose
                variant="inversed"
                onClick={onClose}
                screenReadersText={s__(
                  'EmbedlyAccessibility|Remove embed preview of %{title} from your post',
                ).i__({
                  title,
                })}
              />
            </WrapperBtnClose>
          )}
          <Preview
            aspectRatio={aspectRatio}
            autoplay={autoplay}
            customRenderForEmptyImage={customRenderForEmptyImage}
            embed={embed}
            externalUrl={externalUrl}
            isDisabled={isDisabled}
            isEmbedVideoOnHtml={isEmbedVideoOnHtml}
            isVimeoWhitelistVideo={isVimeoWhitelistVideo}
            openPlayVideo={openPlayVideo}
            roundedEmbed={roundedEmbed}
          />
        </Box>
      )}
      {!hideDescription && (
        <Box flexDirection="column" position="relative" px={3} py={4}>
          {!displayHeader && onClose && (
            <WrapperBtnClose>
              <ButtonClose
                onClick={onClose}
                screenReadersText={s__(
                  'EmbedlyAccessibility|Remove embed preview of %{title} from your post',
                ).i__({
                  title,
                })}
              />
            </WrapperBtnClose>
          )}
          <Title $displayCloseButton={!displayHeader && onClose}>
            <DefaultLink
              href={url ?? embed.url}
              target="_blank"
              styledOnlyOnHover
            >
              <Dotdotdot clamp={2} useNativeClamp>
                {title}
              </Dotdotdot>
            </DefaultLink>
          </Title>
          <Box
            mt={1}
            css={css`
              text-transform: uppercase;
            `}
          >
            {providerUrl && (
              <Dotdotdot clamp={1} useNativeClamp>
                {extractWebsite(providerUrl)}
              </Dotdotdot>
            )}
          </Box>
          <Box mt={2}>
            <Dotdotdot clamp={3} useNativeClamp>
              {description}
            </Dotdotdot>
          </Box>
        </Box>
      )}
    </>
  );
};

/**
 * @deprecated please use `shared/components/DesignSystem/Embed` instead
 */
const EmbedLy = ({ rounded = true, isLoading, ...props }) => {
  if (isLoading) {
    return (
      <WrapperEmbedly rounded={rounded}>
        <Box p={4}>
          <Loader size={60} />
        </Box>
      </WrapperEmbedly>
    );
  }

  const { height, width } = props.embed;

  const aspectRatio = getAspectRatio({ width, height });

  return (
    <WrapperEmbedly rounded={rounded} aspectRatio={aspectRatio}>
      <EmbedlyContent {...props} />
    </WrapperEmbedly>
  );
};

const WrapperEmbedly = ({ children, rounded, aspectRatio }) => (
  <Wrapper
    flexDirection="column"
    data-testid="embedly"
    $rounded={rounded}
    $aspectRatio={aspectRatio}
  >
    {children}
  </Wrapper>
);

EmbedLy.propTypes = {
  autoplay: PropTypes.bool,
  externalUrl: PropTypes.string,
  openPlayVideo: PropTypes.func,
  rounded: PropTypes.bool,
  roundedEmbed: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default EmbedLy;

const Wrapper = styled(Box).attrs(() => ({
  bg: 'smoke',
  flex: 1,
  width: '100%',
}))`
  border-radius: ${({ $rounded }) => ($rounded ? '4px' : '0px')};
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
`;

const WrapperBtnClose = styled(Box)`
  position: absolute;
  right: ${({ theme }) => theme.space[3]}px;
  top: ${({ theme }) => theme.space[3]}px;
  z-index: 2;
`;

const Title = styled(Box).attrs(({ $displayCloseButton }) => ({
  fontWeight: 1,
  pr: $displayCloseButton && 10,
  display: 'block',
}))``;
